import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError, get } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/Layout";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { dt, title } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export default function Settings() {
  const { user, can } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(null);
  const [credential, setCredential] = useState(null);
  const [apiKey, setApiKey] = useState("");

  const { data } = useQuery({ queryKey: ["settings"], queryFn: () => get("/settings") });
  const { data: providers } = useQuery({ queryKey: ["providers"], queryFn: () => get("/providers") });
  const { data: webhooks } = useQuery({ queryKey: ["webhook-events"], queryFn: () => get("/webhooks/events", { page_size: 15 }) });

  useEffect(() => {
    if (data && !form) setForm(data);
  }, [data, form]);

  const save = useMutation({
    mutationFn: () => api.put("/settings", form),
    onSuccess: () => {
      toast.success("Settings saved");
      queryClient.invalidateQueries({ queryKey: ["settings"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const saveCredential = useMutation({
    mutationFn: () =>
      api.post("/providers/credentials", { provider: credential.key, label: credential.name, api_key: apiKey }),
    onSuccess: () => {
      toast.success("Credential encrypted and stored. It is never returned to the browser again.");
      setCredential(null);
      setApiKey("");
      queryClient.invalidateQueries({ queryKey: ["providers"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  if (!form) return <p className="text-sm text-slate-500">Loading settings…</p>;

  return (
    <>
      <PageHeader testId="settings-page" title="Settings" subtitle="Global defaults, compliance policy and provider credentials" />

      <Section title="Global configuration" testId="global-settings">
        <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Company name</Label>
            <Input data-testid="settings-company" value={form.company_name || ""} disabled={!can("settings", "write")} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Default language</Label>
            <Input data-testid="settings-language" value={form.default_language || ""} disabled={!can("settings", "write")} onChange={(e) => setForm({ ...form, default_language: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Maximum call duration (seconds)</Label>
            <Input data-testid="settings-max-duration" type="number" value={form.max_call_duration || 300} disabled={!can("settings", "write")} onChange={(e) => setForm({ ...form, max_call_duration: Number(e.target.value) })} />
          </div>
          <div className="space-y-1.5">
            <Label>Recording retention (days)</Label>
            <Input data-testid="settings-retention" type="number" value={form.recording_retention_days || 90} disabled={!can("settings", "write")} onChange={(e) => setForm({ ...form, recording_retention_days: Number(e.target.value) })} />
          </div>
          <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 md:col-span-2">
            <div>
              <p className="text-sm font-medium text-slate-800">Require AI disclosure at the start of every call</p>
              <p className="text-xs text-slate-500">Calls without disclosure are flagged in quality control.</p>
            </div>
            <Switch
              data-testid="settings-ai-disclosure"
              checked={!!form.ai_disclosure_required}
              disabled={!can("settings", "write")}
              onCheckedChange={(v) => setForm({ ...form, ai_disclosure_required: v })}
            />
          </div>
          {can("settings", "write") ? (
            <Button className="w-fit bg-blue-600 hover:bg-blue-700" disabled={save.isPending} onClick={() => save.mutate()} data-testid="settings-save">
              Save settings
            </Button>
          ) : null}
        </div>
      </Section>

      <Section title="Provider integrations" subtitle="Credentials are encrypted at rest and never returned after saving" testId="providers-section">
        <DataTable
          testId="providers-table"
          columns={[
            { key: "name", label: "Provider", render: (r) => <span className="font-medium text-slate-800">{r.name}</span> },
            { key: "type", label: "Type", render: (r) => title(r.type) },
            { key: "description", label: "Description" },
            { key: "enabled", label: "Enabled", render: (r) => <StatusBadge value={r.enabled ? "completed" : "draft"} label={r.enabled ? "Active" : "Inactive"} /> },
            { key: "configured", label: "Credentials", render: (r) => (r.configured ? <span className="font-mono text-xs">{r.credential_preview}</span> : <span className="text-slate-400">Not configured</span>) },
            {
              key: "actions",
              label: "",
              render: (r) =>
                user?.role === "super_admin" ? (
                  <Button variant="ghost" size="sm" onClick={() => setCredential(r)} data-testid={`provider-configure-${r.key}`}>
                    {r.configured ? "Replace key" : "Add key"}
                  </Button>
                ) : null,
            },
          ]}
          rows={(providers || []).map((p) => ({ ...p, id: p.key }))}
          empty={{ title: "No providers registered" }}
        />
      </Section>

      <Section title="Recent webhook events" subtitle="Raw payloads are stored for debugging; normalized records remain the source of truth" testId="webhooks-section">
        <DataTable
          testId="webhooks-table"
          columns={[
            { key: "provider", label: "Provider", render: (r) => title(r.provider) },
            { key: "event", label: "Normalized event", render: (r) => <span className="font-mono text-xs">{r.event || "unknown"}</span> },
            { key: "provider_event_id", label: "Provider event id", render: (r) => <span className="font-mono text-[11px]">{r.provider_event_id}</span> },
            { key: "processed", label: "Processed", render: (r) => <StatusBadge value={r.processed ? "completed" : "failed"} label={r.processed ? "Processed" : "Ignored"} /> },
            { key: "created_at", label: "Received", render: (r) => dt(r.created_at) },
          ]}
          rows={webhooks?.items || []}
          empty={{ title: "No webhook events recorded" }}
        />
      </Section>

      <Dialog open={!!credential} onOpenChange={(o) => !o && setCredential(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Configure {credential?.name}</DialogTitle></DialogHeader>
          <div className="space-y-1.5">
            <Label>API key</Label>
            <Input data-testid="credential-input" type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="Paste the provider API key" />
            <p className="text-xs text-slate-500">
              The key is encrypted before storage. Only a masked preview is shown afterwards.
            </p>
          </div>
          <DialogFooter>
            <Button className="bg-blue-600 hover:bg-blue-700" disabled={apiKey.length < 6 || saveCredential.isPending} onClick={() => saveCredential.mutate()} data-testid="credential-save">
              Save credential
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
